import {
  AppDocsApiFactory,
  ChatApiFactory,
  Configuration,
  DataSourcesApiFactory,
  DefaultApiFactory,
  FacadesApiFactory,
  InsightsApiFactory,
  Job,
  JobsApiFactory,
  JobStatus,
  PromptsApiFactory,
  ServiceApiFactory,
  TestCasesApiFactory,
} from '@growdash/aisha-client/generated';
import { InitializeService } from '../types/initialize-service';

export const apiFactories = {
  facadesAPI: FacadesApiFactory,
  chatAPI: ChatApiFactory,
  jobsAPI: JobsApiFactory,
  promptsAPI: PromptsApiFactory,
  insightsAPI: InsightsApiFactory,
  dataSourcesAPI: DataSourcesApiFactory,
  appDocsAPI: AppDocsApiFactory,
  defaultAPI: DefaultApiFactory,
  serviceAPI: ServiceApiFactory,
  testCasesAPI: TestCasesApiFactory,
};

export type AishaService = {
  [K in keyof typeof apiFactories]: ReturnType<(typeof apiFactories)[K]>;
};

export const initializeAishaService = (params: InitializeService) => {
  const baseConfig = {
    basePath: params.service_url,
  };
  const apiQueryConfig = new Configuration(baseConfig);
  const { accessToken } = params;
  const config = accessToken
    ? new Configuration({ ...baseConfig, accessToken })
    : apiQueryConfig;
  return Object.fromEntries(
    Object.entries(apiFactories).map(([key, factory]) => [
      key,
      factory(config),
    ]),
  ) as AishaService;
};

// Aisha utils for executing facade requests
export const pollJobStatus = async (
  jobId: string,
  aishaService: AishaService,
  maxAttempts = 2000,
  interval = 500,
): Promise<Job> => {
  let attempts = 0;

  const poll = async (): Promise<Job> => {
    const { jobsAPI } = aishaService;
    const response = await jobsAPI.getJobStatusJobsJobIdGet(jobId);

    if (
      response.data.status === JobStatus.Success ||
      response.data.status === JobStatus.Failure
    ) {
      return response.data;
    }

    if (attempts >= maxAttempts) {
      throw new Error('Polling timeout exceeded');
    }

    attempts++;
    await new Promise((resolve) => setTimeout(resolve, interval));
    return poll();
  };

  return poll();
};

export const executeFacadeRequest = async (
  requestName: string,
  aishaService: AishaService,
  params?: any,
  onError?: (error: string) => void,
  onLoading?: (loading: boolean) => void,
  onResponse?: (response: any) => void,
) => {
  if (!aishaService) return;
  const { facadesAPI } = aishaService;

  try {
    onLoading?.(true);
    const response = await facadesAPI.runFacadeFacadesNameRunPost(requestName, {
      execute_parameters: params,
    });

    const jobResult = await pollJobStatus(response.data.job_id, aishaService);

    if (jobResult?.result?.error_message) {
      onError?.(jobResult.result.error_message);
      throw new Error(
        `Job Error: ${JSON.stringify({
          errorMessage: jobResult.result.error_message,
          errorFields: jobResult.result.error_fields,
        })}`,
      );
    }

    const taskOutput = jobResult?.result?.task_output;
    onResponse?.(taskOutput);
    return taskOutput;
  } catch (error) {
    console.error('Error executing facade request:', error);
    throw error;
  } finally {
    onLoading?.(false);
  }
};
